import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid as Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AutoAwesome,
  FilterList,
  FormatQuote,
  Refresh,
  Search,
  Star,
  Storefront,
  Tune,
  VerifiedUser,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { SellerReview, sellerReviewService } from "../services/sellerReviews";
import PageTitle from "../components/PageTitle";
import ReviewCommentBody from "../components/ReviewCommentBody";
import { ReviewCriteriaGrid } from "../components/ReviewCriteriaRatings";

type RatingFilter = "all" | "4+" | "3+" | "2-";
type SortOption = "newest" | "oldest" | "highest" | "lowest";

const PAGE_SIZE = 9;

const Reviews: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [reviews, setReviews] = useState<SellerReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>("all");
  const [commentFilter, setCommentFilter] = useState("all");
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [expandedCommentIds, setExpandedCommentIds] = useState<Set<string>>(new Set());

  const setCommentExpanded = (reviewId: string, expanded: boolean) => {
    setExpandedCommentIds((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.add(reviewId);
      } else {
        next.delete(reviewId);
      }
      return next;
    });
  };

  const fetchReviews = async (showRefreshState: boolean = false) => {
    if (showRefreshState) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }

    setError(null);

    try {
      const { data, error: fetchError } = await sellerReviewService.getAllReviews(1, 100);

      if (fetchError) {
        throw fetchError;
      }

      setReviews(data || []);
    } catch (err) {
      console.error("Error fetching reviews:", err);
      setError(
        t("reviews.fetchError", {
          defaultValue: "Failed to load reviews",
        })
      );
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    void fetchReviews();
  }, [t]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, ratingFilter, commentFilter, sortBy]);

  const filteredReviews = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    const matchesQuery = (review: SellerReview) => {
      if (!normalizedQuery) {
        return true;
      }

      const sellerName = review.display_seller_name || review.seller?.company_name || review.seller?.full_name || "";
      const reviewerName = review.reviewer?.full_name || "";
      const comment = review.comment || "";

      return [sellerName, reviewerName, comment].some((value) =>
        value.toLowerCase().includes(normalizedQuery)
      );
    };

    const matchesRating = (review: SellerReview) => {
      if (ratingFilter === "all") {
        return true;
      }

      if (ratingFilter === "4+") {
        return review.overall_rating >= 4;
      }

      if (ratingFilter === "3+") {
        return review.overall_rating >= 3;
      }

      return review.overall_rating <= 2.99;
    };

    const matchesComment = (review: SellerReview) => {
      if (commentFilter === "all") {
        return true;
      }

      if (commentFilter === "with-comment") {
        return Boolean(review.comment?.trim());
      }

      return !review.comment?.trim();
    };

    const sorted = [...reviews]
      .filter((review) => matchesQuery(review) && matchesRating(review) && matchesComment(review))
      .sort((a, b) => {
        if (sortBy === "oldest") {
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        }

        if (sortBy === "highest") {
          return b.overall_rating - a.overall_rating;
        }

        if (sortBy === "lowest") {
          return a.overall_rating - b.overall_rating;
        }

        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

    return sorted;
  }, [commentFilter, ratingFilter, reviews, searchQuery, sortBy]);

  const totalCount = reviews.length;
  const filteredCount = filteredReviews.length;
  const totalPages = Math.max(1, Math.ceil(filteredCount / PAGE_SIZE));
  const paginatedReviews = filteredReviews.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const averageRating = useMemo(() => {
    if (filteredReviews.length === 0) {
      return 0;
    }

    const total = filteredReviews.reduce((sum, review) => sum + review.overall_rating, 0);
    return total / filteredReviews.length;
  }, [filteredReviews]);

  const commentedCount = useMemo(
    () => reviews.filter((review) => Boolean(review.comment?.trim())).length,
    [reviews]
  );

  const topRatedCount = useMemo(
    () => reviews.filter((review) => review.overall_rating >= 4).length,
    [reviews]
  );

  const handleRefresh = () => {
    void fetchReviews(true);
  };

  const resetFilters = () => {
    setSearchQuery("");
    setRatingFilter("all");
    setCommentFilter("all");
    setSortBy("newest");
    setPage(1);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8, minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: { xs: 5, md: 7 },
        background: isDark
          ? `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.96)} 0%, ${alpha(
              theme.palette.primary.dark,
              0.12
            )} 100%)`
          : `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(
              theme.palette.background.default,
              0.98
            )} 100%)`,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={4.5}>
          <Box
            sx={{
              borderRadius: 5,
              px: { xs: 2.5, md: 4 },
              py: { xs: 3, md: 4 },
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                isDark ? 0.18 : 0.1
              )} 0%, ${alpha(theme.palette.secondary.main, isDark ? 0.16 : 0.08)} 100%)`,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.16),
              boxShadow: `0 18px 48px ${alpha(theme.palette.common.black, isDark ? 0.3 : 0.08)}`,
            }}
          >
            <Stack spacing={3}>
              <PageTitle
                text={t("reviews.pageTitle", { defaultValue: "All Seller Reviews" })}
                align="center"
                size="large"
              />

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  maxWidth: 760,
                  mx: "auto",
                  textAlign: "center",
                }}
              >
                {t("reviews.pageDescription", {
                  defaultValue:
                    "Browse trusted seller feedback, discover top-rated suppliers, and explore detailed customer experiences.",
                })}
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <StatCard
                    icon={<AutoAwesome />}
                    label={t("reviews.totalReviews", { defaultValue: "Total reviews" })}
                    value={totalCount.toString()}
                    themeColor={theme.palette.primary.main}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <StatCard
                    icon={<Star />}
                    label={t("reviews.averageScore", { defaultValue: "Average score" })}
                    value={averageRating ? averageRating.toFixed(1) : "0.0"}
                    themeColor={theme.palette.warning.main}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <StatCard
                    icon={<FormatQuote />}
                    label={t("reviews.withComments", { defaultValue: "With comments" })}
                    value={commentedCount.toString()}
                    themeColor={theme.palette.info.main}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <StatCard
                    icon={<VerifiedUser />}
                    label={t("reviews.topRated", { defaultValue: "Top rated 4+" })}
                    value={topRatedCount.toString()}
                    themeColor={theme.palette.success.main}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Box>

          <Card
            sx={{
              borderRadius: 5,
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.12),
              boxShadow: `0 12px 36px ${alpha(theme.palette.common.black, isDark ? 0.22 : 0.06)}`,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Stack spacing={3}>
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", lg: "center" }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: "primary.main",
                        width: 44,
                        height: 44,
                      }}
                    >
                      <FilterList />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {t("reviews.filterTitle", { defaultValue: "Search & filter reviews" })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("reviews.filterSubtitle", {
                          defaultValue:
                            "Use search, ratings, and comment filters to find the most relevant feedback.",
                        })}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ width: "100%", alignItems: { xs: "stretch", sm: "center" } }}
                  >
                    <Chip
                      color="primary"
                      variant="outlined"
                      label={t("reviews.showingResults", {
                        defaultValue: `Showing ${filteredCount} results`,
                        count: filteredCount,
                      })}
                      sx={{ fontWeight: 700, maxWidth: "100%", ".MuiChip-label": { px: 1.5 } }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                        textTransform: "none",
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      {isRefreshing
                        ? t("common.loading", { defaultValue: "Loading..." })
                        : t("common.refresh", { defaultValue: "Refresh" })}
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<Tune />}
                      onClick={resetFilters}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                        textTransform: "none",
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      {t("common.reset", { defaultValue: "Reset" })}
                    </Button>
                  </Stack>
                </Stack>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6, lg: 5 }}>
                    <TextField
                      fullWidth
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder={t("reviews.searchPlaceholder", {
                        defaultValue: "Search sellers, reviewers, or comments...",
                      })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, lg: 2.333 }} sx={{ minWidth: 0 }}>
                    <TextField
                      select
                      fullWidth
                      label={t("reviews.ratingFilter", { defaultValue: "Rating" })}
                      value={ratingFilter}
                      onChange={(event) => setRatingFilter(event.target.value as RatingFilter)}
                    >
                      <MenuItem value="all">
                        {t("reviews.allRatings", { defaultValue: "All ratings" })}
                      </MenuItem>
                      <MenuItem value="4+">
                        {t("reviews.ratingFourPlus", { defaultValue: "4 stars & above" })}
                      </MenuItem>
                      <MenuItem value="3+">
                        {t("reviews.ratingThreePlus", { defaultValue: "3 stars & above" })}
                      </MenuItem>
                      <MenuItem value="2-">
                        {t("reviews.ratingTwoBelow", { defaultValue: "Below 3 stars" })}
                      </MenuItem>
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, lg: 2.333 }} sx={{ minWidth: 0 }}>
                    <TextField
                      select
                      fullWidth
                      label={t("reviews.commentFilter", { defaultValue: "Comments" })}
                      value={commentFilter}
                      onChange={(event) => setCommentFilter(event.target.value)}
                    >
                      <MenuItem value="all">
                        {t("reviews.allComments", { defaultValue: "All reviews" })}
                      </MenuItem>
                      <MenuItem value="with-comment">
                        {t("reviews.withCommentsOnly", { defaultValue: "With comments" })}
                      </MenuItem>
                      <MenuItem value="without-comment">
                        {t("reviews.withoutComments", { defaultValue: "Without comments" })}
                      </MenuItem>
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, lg: 2.333 }} sx={{ minWidth: 0 }}>
                    <TextField
                      select
                      fullWidth
                      label={t("reviews.sortBy", { defaultValue: "Sort by" })}
                      value={sortBy}
                      onChange={(event) => setSortBy(event.target.value as SortOption)}
                    >
                      <MenuItem value="newest">
                        {t("reviews.sortNewest", { defaultValue: "Newest first" })}
                      </MenuItem>
                      <MenuItem value="oldest">
                        {t("reviews.sortOldest", { defaultValue: "Oldest first" })}
                      </MenuItem>
                      <MenuItem value="highest">
                        {t("reviews.sortHighest", { defaultValue: "Highest rating" })}
                      </MenuItem>
                      <MenuItem value="lowest">
                        {t("reviews.sortLowest", { defaultValue: "Lowest rating" })}
                      </MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          {error ? (
            <Alert severity="error">{error}</Alert>
          ) : paginatedReviews.length === 0 ? (
            <Card
              sx={{
                borderRadius: 5,
                border: "1px dashed",
                borderColor: alpha(theme.palette.primary.main, 0.2),
                boxShadow: "none",
              }}
            >
              <CardContent sx={{ py: 8 }}>
                <Stack spacing={2} alignItems="center" textAlign="center">
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      color: "primary.main",
                    }}
                  >
                    <Search />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {t("reviews.noReviews", { defaultValue: "No reviews found." })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
                    {t("reviews.noReviewsDescription", {
                      defaultValue:
                        "Try changing your search keywords or filters to discover more seller reviews.",
                    })}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <>
              <Grid container spacing={3}>
                {paginatedReviews.map((review) => {
                  const sellerName =
                    review.display_seller_name ||
                    review.seller?.company_name ||
                    review.seller?.full_name ||
                    t("reviews.unknownSeller", { defaultValue: "Unknown Seller" });

                  const reviewerName =
                    review.display_reviewer_name ||
                    review.reviewer?.full_name ||
                    t("reviews.unknownReviewer", { defaultValue: "Unknown Reviewer" });

                  const isCommentExpanded = expandedCommentIds.has(review.id);

                  return (
                    <Grid key={review.id} size={{ xs: 12, md: 6, xl: 4 }} sx={{ display: "flex", minWidth: 0 }}>
                      <Card
                        sx={{
                          minHeight: 520,
                          height: isCommentExpanded ? "auto" : 520,
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 4,
                          border: "1px solid",
                          borderColor: alpha(theme.palette.primary.main, 0.14),
                          boxShadow: `0 14px 34px ${alpha(theme.palette.common.black, isDark ? 0.22 : 0.08)}`,
                          background: `linear-gradient(180deg, ${alpha(
                            theme.palette.primary.light,
                            isDark ? 0.08 : 0.06
                          )} 0%, ${theme.palette.background.paper} 28%)`,
                          transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, height 0.2s ease",
                          overflow: isCommentExpanded ? "visible" : "hidden",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: `0 20px 44px ${alpha(theme.palette.common.black, isDark ? 0.3 : 0.14)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.28),
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            p: { xs: 2, sm: 3 },
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Stack spacing={2.5} sx={{ height: "100%" }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: { xs: "stretch", sm: "flex-start" },
                                justifyContent: "space-between",
                                gap: 2,
                                flexDirection: { xs: "column", sm: "row" },
                              }}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                                <Avatar
                                  sx={{
                                    width: 52,
                                    height: 52,
                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                    color: "primary.main",
                                    fontWeight: 700,
                                  }}
                                >
                                  <Storefront />
                                </Avatar>

                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 800,
                                      color: "text.primary",
                                      lineHeight: 1.2,
                                      mb: 0.5,
                                      fontSize: { xs: "1rem", sm: "1.25rem" },
                                      wordBreak: "break-word",
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    {sellerName}
                                  </Typography>

                                  <Stack
                                    direction="column"
                                    spacing={0.5}
                                  >
                                    <Typography variant="body2" color="text.secondary">
                                      {t("reviews.reviewedBy", { defaultValue: "Reviewed by" })}: {reviewerName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(review.created_at).toLocaleDateString()}
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Stack>

                              <Chip
                                icon={<Star sx={{ color: "#fff !important" }} />}
                                label={`${review.overall_rating.toFixed(1)} / 5`}
                                sx={{
                                  flexShrink: 0,
                                  height: 36,
                                  borderRadius: 999,
                                  px: 1,
                                  fontWeight: 800,
                                  color: theme.palette.common.white,
                                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                  boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, 0.28)}`,
                                  alignSelf: { xs: "stretch", sm: "auto" },
                                  width: { xs: "100%", sm: "auto" },
                                  justifyContent: "center",
                                }}
                              />
                            </Box>

                            <ReviewCriteriaGrid review={review} />

                            <Box
                              sx={{
                                flex: isCommentExpanded ? "0 0 auto" : 1,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: isCommentExpanded ? "flex-start" : "space-between",
                                minHeight: 0,
                              }}
                            >
                              {review.comment ? (
                                <Box
                                  sx={{
                                    position: "relative",
                                    p: 2,
                                    pl: 2.5,
                                    borderRadius: 3,
                                    bgcolor: alpha(theme.palette.info.main, 0.05),
                                    border: "1px solid",
                                    borderColor: alpha(theme.palette.info.main, 0.12),
                                  }}
                                >
                                  <FormatQuote
                                    sx={{
                                      position: "absolute",
                                      top: 12,
                                      right: 12,
                                      fontSize: 30,
                                      color: alpha(theme.palette.info.main, 0.22),
                                    }}
                                  />
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    {t("reviews.customerFeedback", { defaultValue: "Customer feedback" })}
                                  </Typography>
                                  <ReviewCommentBody
                                    comment={review.comment}
                                    maxLines={4}
                                    onExpandedChange={(expanded) => setCommentExpanded(review.id, expanded)}
                                    typographyProps={{
                                      sx: {
                                        lineHeight: 1.8,
                                        pr: 1.5,
                                      },
                                    }}
                                  />
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    bgcolor: alpha(theme.palette.warning.main, 0.06),
                                    border: "1px dashed",
                                    borderColor: alpha(theme.palette.warning.main, 0.2),
                                    minHeight: 140,
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    {t("reviews.noCommentProvided", {
                                      defaultValue: "This reviewer submitted ratings without a written comment.",
                                    })}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              justifyContent="space-between"
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              spacing={1.5}
                            >
                              <Chip
                                label={review.comment?.trim()
                                  ? t("reviews.verifiedFeedback", { defaultValue: "Detailed feedback" })
                                  : t("reviews.ratingOnly", { defaultValue: "Rating only" })}
                                size="small"
                                variant="outlined"
                                color={review.comment?.trim() ? "success" : "default"}
                                sx={{ fontWeight: 700 }}
                              />
                              <Rating value={review.overall_rating} precision={0.5} readOnly />
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <Stack spacing={1.5} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t("reviews.paginationSummary", {
                        defaultValue: `Page ${page} of ${totalPages}`,
                      })}
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      color="primary"
                      size="medium"
                      shape="rounded"
                    />
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  themeColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, themeColor }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: 3,
      border: "1px solid",
      borderColor: alpha(themeColor, 0.18),
      boxShadow: "none",
      bgcolor: alpha(themeColor, 0.06),
    }}
  >
    <CardContent sx={{ p: 2.25 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            bgcolor: alpha(themeColor, 0.14),
            color: themeColor,
            width: 44,
            height: 44,
          }}
        >
          {icon}
        </Avatar>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

export default Reviews;